// Colour constants as vec3 nodes. three's typings keep 'color' and 'vec3' node types apart even
// though the shader treats them alike; this is the one place that bridges them.
//
// `screen()` is for palettes authored as the colour you want to SEE on flat, lit ground. The
// engine tone-maps with Khronos PBR Neutral, which subtracts an offset (up to 0.04 linear) from all
// three channels to deepen blacks. On a mid green whose blue channel is ~0.04, that offset wipes
// blue out and the map turns lime. screen() adds the offset back and divides out the flat-ground
// light, so the palette hex is (close to) the pixel on screen.
//
//     screen colour ──► + neutral offset ──► ÷ FLAT_LIGHT ──► albedo
//     albedo ──► painted light (≈ FLAT_LIGHT on flat ground) ──► Neutral tone map ──► screen colour

import * as THREE from 'three/webgpu';
import { color, max, min, select, sqrt } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';

// PBR Neutral black offset: offset = x − 6.25x² below x = 0.08, then a flat 0.04 (x = min channel).
const NEUTRAL_KNEE = 0.08;
const NEUTRAL_OFFSET = 0.04;
const NEUTRAL_CURVE = 6.25;

/**
 * Linear light that flat ground receives from the rig in lighting.ts (key ramp + fill), measured
 * from renders: desert albedo #e3c690 rendered as #dbb376.
 */
const FLAT_LIGHT = 0.97;

/** A constant linear colour from an sRGB hex string, typed as vec3. */
export function rgb(hex: string): Node<'vec3'> {
  return color(hex) as unknown as Node<'vec3'>;
}

/** The sRGB albedo hex that renders as `hex` on flat lit ground (see the header). */
function albedoFor(hex: string): string {
  const c = new THREE.Color(hex);
  const m = Math.min(c.r, c.g, c.b);
  const outKnee = NEUTRAL_KNEE - NEUTRAL_OFFSET;
  const pre = m >= outKnee ? m + NEUTRAL_OFFSET : Math.sqrt(m / NEUTRAL_CURVE);
  const offset = pre - m;
  c.setRGB((c.r + offset) / FLAT_LIGHT, (c.g + offset) / FLAT_LIGHT, (c.b + offset) / FLAT_LIGHT, THREE.LinearSRGBColorSpace);
  return `#${c.getHexString()}`;
}

/** A palette colour authored as the on-screen result, as an albedo vec3 node. */
export function screen(hex: string): Node<'vec3'> {
  return rgb(albedoFor(hex));
}

/**
 * screen() for colours known only in the shader (vertex colours of props, authored as on-screen
 * palette swatches in linear RGB): the same offset-and-light correction, per fragment.
 */
export function screenNode(c: Node<'vec3'>): Node<'vec3'> {
  const m = min(c.x, min(c.y, c.z)).max(0);
  const outKnee = NEUTRAL_KNEE - NEUTRAL_OFFSET;
  const pre = select(m.greaterThanEqual(outKnee), m.add(NEUTRAL_OFFSET), sqrt(m.div(NEUTRAL_CURVE)));
  return max(c.add(pre.sub(m)), 0).div(FLAT_LIGHT);
}
